import React, { useState, useEffect } from 'react';
import { X, TrendingUp, TrendingDown, ChevronRight, Info } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import axios from 'axios';

function PillarDrillDown({ pillarId, pillarName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selectedSubCat, setSelectedSubCat] = useState(null);

  const pillarColors = {
    community: '#3b82f6',
    authenticity: '#a855f7',
    accountability: '#10b981'
  };

  const color = pillarColors[pillarName?.toLowerCase()] || '#9333ea';

  useEffect(() => {
    if (pillarId) {
      loadPillarData();
    }
  }, [pillarId]);

  const loadPillarData = async () => {
    if (!pillarId) {
      setError('No pillar ID provided');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      console.log('Loading pillar data for:', pillarId);
      const response = await axios.get(`/dashboard/pillars/${pillarId}/breakdown`);
      console.log('Pillar data loaded:', response.data);
      setData(response.data);
    } catch (err) {
      console.error('Pillar drill-down error:', err);
      console.error('Error details:', err.response?.status, err.response?.data);
      setError(err.response?.data?.detail || err.message || 'Failed to load pillar details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="pillar-drilldown-modal">
        <div className="bg-white rounded-2xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data?.has_data) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="pillar-drilldown-modal">
        <div className="bg-white rounded-2xl p-8 max-w-4xl w-full mx-4">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{pillarName} Details</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-testid="close-drilldown">
              <X size={24} />
            </button>
          </div>
          <div className="text-center py-12">
            <Info className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">{error || 'No data available for this pillar'}</p>
          </div>
        </div>
      </div>
    );
  }

  const chartData = data.sub_categories?.map(subCat => ({
    name: subCat.name,
    score: subCat.score || 0,
    weight: (subCat.weight * 100).toFixed(0)
  })) || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="pillar-drilldown-modal">
      <div className="bg-white rounded-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div style={{ backgroundColor: color }} className="p-6 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-1">{data.pillar?.name || pillarName}</h2>
              <p className="text-white/80">{data.pillar?.description}</p>
            </div>
            <button 
              onClick={onClose} 
              className="text-white/80 hover:text-white transition"
              data-testid="close-drilldown"
            >
              <X size={24} />
            </button>
          </div>
          
          <div className="mt-6 flex items-baseline gap-4">
            <div className="text-5xl font-bold">{data.score?.toFixed(1) || '0.0'}</div>
            <div className="text-white/80">
              <span className="text-lg">out of 100</span>
              <span className="ml-3 text-sm">Weight: {((data.weight || 0.33) * 100).toFixed(0)}%</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {/* Sub-categories Chart */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Sub-Category Breakdown</h3>
            <div className="h-64 bg-gray-50 rounded-xl p-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis type="category" dataKey="name" width={120} />
                  <Tooltip 
                    formatter={(value) => [value.toFixed(1), 'Score']}
                    contentStyle={{ 
                      background: 'white', 
                      border: 'none', 
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={color} fillOpacity={0.7 + (index * 0.1)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Sub-categories List with Metrics */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Metrics</h3>
            <div className="space-y-4">
              {data.sub_categories?.map((subCat, idx) => (
                <div 
                  key={idx} 
                  className="border border-gray-200 rounded-xl overflow-hidden"
                  data-testid={`subcategory-${subCat.name?.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <button
                    onClick={() => setSelectedSubCat(selectedSubCat === idx ? null : idx)}
                    className="w-full flex justify-between items-center p-4 hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: color }}
                      ></div>
                      <div className="text-left">
                        <p className="font-semibold text-gray-900">{subCat.name}</p>
                        <p className="text-sm text-gray-500">{subCat.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">{subCat.score?.toFixed(1) || '—'}</p>
                        <p className="text-xs text-gray-500">Weight: {(subCat.weight * 100).toFixed(0)}%</p>
                      </div>
                      <ChevronRight 
                        className={`w-5 h-5 text-gray-400 transition-transform ${selectedSubCat === idx ? 'rotate-90' : ''}`}
                      />
                    </div>
                  </button>

                  {selectedSubCat === idx && subCat.metrics && (
                    <div className="px-4 pb-4 border-t border-gray-100">
                      <div className="mt-4 space-y-3">
                        {subCat.metrics.map((metric, mIdx) => (
                          <div 
                            key={mIdx} 
                            className="flex justify-between items-center py-3 px-4 bg-gray-50 rounded-lg"
                            data-testid={`metric-${metric.name?.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <div>
                              <p className="font-medium text-gray-900">{metric.name}</p>
                              <p className="text-xs text-gray-500">
                                {metric.data_type} • Weight: {(metric.weight * 100).toFixed(0)}%
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-gray-900">
                                {metric.normalized_value?.toFixed(1) || metric.raw_value?.toFixed(1) || '—'}
                              </p>
                              {metric.raw_value && (
                                <p className="text-xs text-gray-500">Raw: {metric.raw_value}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PillarDrillDown;
